import React, { useContext, useState } from 'react';
import { ApolloError, QueryResult, useLazyQuery } from '@apollo/client';
import query from './Querys/EkonomiskAnalysInsikterOchRekommendationer.gql';
import { EkonomiskAnalysContext } from '../Context/EkonomiskAnalysContext';
import { ISammanfattningRows } from './EkonomiskAnalysSammanfattning';
import {
  getAIMessagesText,
  parseAIResponse,
} from '../../Utils/detaljanalysUtils';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { EDCard } from '../../../../../../../BaseComponents/Card/EDCard';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import EDList from '../../../../../../../BaseComponents/List/EDList';
import EDListItem from '../../../../../../../BaseComponents/List/Components/EDListItem';
import EDSkeleton from '../../../../../../../BaseComponents/Skeleton/EDSkeleton';
import { EDControlledExpansionPanel } from '../../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import { Theme, useTheme } from '@mui/material/styles';
import EkonomiskAnalysExpansionLabel from './Components/EkonomiskAnalysSammanfattningCharts/Components/EkonomiskAnalysExpansionLabel';
import { AnalysTypeEnum } from '../../Const/EkonomiskAnalysConsts';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';
import {
  EkonomiskAnalysDetaljAiActive,
  EkonomiskAnalysDetaljAiActiveVariables,
} from './Querys/_QueryTypes/EkonomiskAnalysDetaljAiActive';

interface IProps {
  analysType: AnalysTypeEnum;
  tableRows: ISammanfattningRows[] | undefined;
  expandedAIInsights: boolean;
  setExpandedAIInsights: (expanded: boolean) => void;
  aiPrompt: string;
  aiActiveQueryResult: QueryResult<
    EkonomiskAnalysDetaljAiActive,
    EkonomiskAnalysDetaljAiActiveVariables
  >;
}

export interface IAIQueryResult {
  insikter: { rubrik: string; beskrivning: string }[];
  rekommendationer: { rubrik: string; beskrivning: string }[];
  kommentar: string;
}

interface IData {
  ek_ekanalys_ai_insikter_rekommendationer: {
    insikter_och_rekommendationer: string | null;
  };
}

interface IVariables {
  dbname: string;
  messagetext: string;
  context: string;
}

export const SammanfattningInsikterRekommendationer: React.FC<
  IProps
> = props => {
  const { dbName } = useContext(EkonomiskAnalysContext);
  const theme = useTheme<Theme>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const aiIsActive = Boolean(
    !props.aiActiveQueryResult.loading &&
      props.aiActiveQueryResult.data?.ek_ekanalys_ai_active?.active
  );

  const canAnalyze = Boolean(
    aiIsActive && dbName && props.tableRows !== undefined
  );

  const [fetchAiInsights, queryResult] = useLazyQuery<IData, IVariables>(
    query,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        setErrorMessage(null);
      },
      onError: (error: ApolloError) => {
        const guiError = error.graphQLErrors?.find(
          graphQLError => typeof graphQLError.extensions?.GUIError === 'string'
        )?.extensions?.GUIError;

        if (typeof guiError === 'string' && guiError.length > 0) {
          setErrorMessage(guiError);
          return;
        }

        setErrorMessage(error.message || 'Kunde inte hämta AI-insikter.');
      },
    }
  );

  const isAnalyzing = queryResult.loading || props.aiActiveQueryResult.loading;
  const aiInactiveMessage =
    !props.aiActiveQueryResult.loading && !aiIsActive
      ? props.aiActiveQueryResult.data?.ek_ekanalys_ai_active?.message
      : null;
  const overlayMessage = errorMessage || aiInactiveMessage;

  const handleAnalyze = () => {
    if (!canAnalyze) {
      return;
    }

    setErrorMessage(null);

    fetchAiInsights({
      variables: {
        dbname: dbName || '',
        messagetext: getAIMessagesText(props.aiPrompt),
        context: JSON.stringify(props.tableRows),
      },
    });
  };

  const resultInJson: IAIQueryResult | null =
    queryResult.data &&
    queryResult.data.ek_ekanalys_ai_insikter_rekommendationer
      .insikter_och_rekommendationer
      ? parseAIResponse(
          queryResult.data.ek_ekanalys_ai_insikter_rekommendationer
            .insikter_och_rekommendationer
        )
      : null;

  const showAnalyzeButton =
    !overlayMessage && aiIsActive && !isAnalyzing && !resultInJson;

  return (
    <EDControlledExpansionPanel
      borderVisible
      headerColor={theme.customProperties.MenuList.baseBg}
      expanded={props.expandedAIInsights}
      expandIconColor="primary"
      onChange={(_, newValue) => props.setExpandedAIInsights(newValue)}
      label={
        <EkonomiskAnalysExpansionLabel
          labelText="AI-insikter & rekommendationer"
          iconType="tipsAndUpdates"
          iconVerticalAlign="sub"
        />
      }
      isCompact
    >
      <EDBox
        sx={{
          minHeight: '300px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <EDTypography
          sx={theme => ({
            textAlign: 'center',
            color: theme.palette.text.secondary,
            mb: 1,
          })}
        >
          Baserat på ekonomisk data genereras automatiskt nyckelinsikter och
          rekommendationer som kan hjälpa er att identifiera avvikelser, trender
          och möjliga åtgärder.
        </EDTypography>

        <EDBox
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            margin: '10px',

            flex: 1,
          }}
        >
          <EDBox
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              width: '100%',
              height: '100%',
              flex: 1,
            }}
          >
            <EDBox flex={1}>
              <EDCard
                backgroundColor={
                  theme.customProperties.customDetaljanalys.canvas
                }
                height="100%"
                aiInsightsElevated
                minHeight={isAnalyzing ? '220px' : undefined}
              >
                <EDBox
                  sx={{
                    pt: 2,
                    pb: 2,
                    pl: 2.5,
                    pr: 2.5,
                  }}
                >
                  <EDBox
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <EDBox
                      sx={theme => ({
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.common.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      })}
                    >
                      <EDIcon
                        iconType="insights"
                        color="primary"
                        customSvgColor={theme.palette.primary.main}
                        className="print-hidden-cell"
                      />
                    </EDBox>
                    <EDBox
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                      }}
                    >
                      <EDTypography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, lineHeight: 1.2 }}
                      >
                        Nyckelinsikter
                      </EDTypography>
                    </EDBox>
                  </EDBox>
                  {resultInJson ? (
                    <EDList>
                      {resultInJson.insikter.map((insikt, index) => (
                        <EDListItem key={index}>
                          <EDBox>
                            <EDTypography bold component={'span'}>
                              {`${insikt.rubrik}: `}
                            </EDTypography>
                            <EDTypography component={'span'}>
                              {` ${insikt.beskrivning}`}
                            </EDTypography>
                          </EDBox>
                        </EDListItem>
                      ))}
                    </EDList>
                  ) : (
                    <PlaceholderRows isAnalyzing={isAnalyzing} />
                  )}
                </EDBox>
              </EDCard>
            </EDBox>
            <EDBox flex={1}>
              <EDCard
                aiInsightsElevated
                backgroundColor={
                  theme.customProperties.customDetaljanalys.canvas
                }
                height="100%"
                minHeight={isAnalyzing ? '220px' : undefined}
              >
                <EDBox sx={{ pt: 2, pb: 2, pl: 2.5, pr: 2.5 }}>
                  <EDBox
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <EDBox
                      sx={theme => ({
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.common.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      })}
                    >
                      <EDIcon
                        iconType="tasks"
                        color="primary"
                        customSvgColor={theme.palette.primary.main}
                        className="print-hidden-cell"
                      />
                    </EDBox>
                    <EDBox
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                      }}
                    >
                      <EDTypography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, lineHeight: 1.2 }}
                      >
                        Rekommendationer
                      </EDTypography>
                    </EDBox>
                  </EDBox>
                  {resultInJson ? (
                    <EDList>
                      {resultInJson.rekommendationer.map(
                        (rekommendation, index) => (
                          <EDListItem key={index}>
                            <EDBox>
                              <EDTypography bold component={'span'}>
                                {`${rekommendation.rubrik}: `}
                              </EDTypography>
                              <EDTypography component={'span'}>
                                {` ${rekommendation.beskrivning}`}
                              </EDTypography>
                            </EDBox>
                          </EDListItem>
                        )
                      )}
                    </EDList>
                  ) : (
                    <PlaceholderRows isAnalyzing={isAnalyzing} />
                  )}
                </EDBox>
              </EDCard>
            </EDBox>
          </EDBox>
        </EDBox>
        {overlayMessage && (
          <EDBox
            sx={{
              border: '2px dotted black',
              borderColor: theme => theme.palette.divider,
              borderRadius: '5px',
              display: 'flex',
              justifyContent: 'center',
              mt: 1,
            }}
          >
            <EDTypography variant="h6" color="primary">
              {overlayMessage}
            </EDTypography>
          </EDBox>
        )}
        {showAnalyzeButton && (
          <EDBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
            }}
          >
            <EDButton
              label="Analysera"
              onClick={handleAnalyze}
              disabled={!canAnalyze || isAnalyzing}
              primary
              icon={
                <EDIcon
                  iconType="tipsAndUpdates"
                  customSvgColor={theme.palette.common.white}
                  className="print-hidden-cell"
                />
              }
            />
          </EDBox>
        )}
        <EDTypography
          variant="body2"
          sx={theme => ({
            textAlign: 'center',
            fontStyle: 'italic',
            color: theme.palette.text.secondary,
            mt: 1,
          })}
        >
          Observera att AI-genererat innehåll kan innehålla felaktigheter och
          bör granskas.
        </EDTypography>
      </EDBox>
    </EDControlledExpansionPanel>
  );
};

interface IPlaceholderRowsProps {
  isAnalyzing: boolean;
}

const PlaceholderRows: React.FC<IPlaceholderRowsProps> = ({ isAnalyzing }) => (
  <EDBox
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      pl: 1,
      pr: 1,
      pt: 0.5,
    }}
  >
    {[0, 1, 2].map(index => (
      <EDBox
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <EDBox
          sx={theme => ({
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: theme.palette.grey[400],
            flexShrink: 0,
          })}
        />
        <EDBox sx={{ flex: 1 }}>
          <EDSkeleton
            variant="rectangular"
            height={10}
            width={`${85 - index * 10}%`}
            animation={isAnalyzing ? 'pulse' : false}
            sx={{ borderRadius: '4px' }}
          />
        </EDBox>
      </EDBox>
    ))}
  </EDBox>
);
